// lzapps_common_code/utils/deviceDetection.js

/**
 * Detect if the user is on a mobile device
 * @returns {boolean} True if mobile device
 */
export const isMobile = () => {
    if (typeof window === 'undefined') return false;
    
    const userAgent = window.navigator.userAgent || '';
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    
    return mobileRegex.test(userAgent) || window.innerWidth <= 768;
  };
  
  /**
   * Detect if the user is on a tablet
   * @returns {boolean} True if tablet device
   */
  export const isTablet = () => {
    if (typeof window === 'undefined') return false;
    
    const userAgent = window.navigator.userAgent || '';
    const tabletRegex = /iPad|Android(?!.*Mobile)|Kindle|Silk/i;
    
    return tabletRegex.test(userAgent) || (window.innerWidth > 768 && window.innerWidth <= 1024);
  };
  
  /**
   * Detect if the user is on iOS
   * @returns {boolean} True if iOS device
   */
  export const isIOS = () => {
    if (typeof window === 'undefined') return false;
    
    const userAgent = window.navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(userAgent);
  };
  
  /**
   * Detect if the user is on Android
   * @returns {boolean} True if Android device
   */
  export const isAndroid = () => {
    if (typeof window === 'undefined') return false;
    
    const userAgent = window.navigator.userAgent || '';
    return /Android/.test(userAgent);
  };
  
  /**
   * Get the user's platform for app store redirects
   * @returns {string} 'ios', 'android', or 'web'
   */
  export const detectDevice = () => {
    if (isIOS()) return 'ios';
    if (isAndroid()) return 'android';
    return 'web';
  };
  
  /**
   * Get device-specific app store URL
   * @param {Object} appStore - Object with ios and android URLs
   * @returns {string} Appropriate app store URL
   */
  export const getAppStoreUrl = (appStore) => {
    const device = detectDevice();
    return appStore[device] || appStore.ios;
  };
  
  /**
   * Detect if user is likely to prefer mobile app over web
   * @returns {boolean} True if should show mobile app prompt
   */
  export const shouldShowMobileAppPrompt = () => {
    if (!isMobile()) return false;
    
    // Don't show if already installed (check for standalone mode)
    if (window.navigator.standalone) return false;
    
    // Don't show if in WebView (common in social media apps)
    const userAgent = window.navigator.userAgent || '';
    if (/FBAN|FBAV|Instagram|Twitter|LinkedIn/i.test(userAgent)) return false;
    
    // Check if user has dismissed banner recently
    const dismissed = localStorage.getItem('mobileAppBannerDismissed');
    if (dismissed) {
      const dismissTime = parseInt(dismissed, 10);
      const hoursSinceDismiss = (Date.now() - dismissTime) / (1000 * 60 * 60);
      if (hoursSinceDismiss < 24) return false; // Don't show for 24 hours
    }
    
    return true;
  };
  
  /**
   * Mark mobile app banner as dismissed
   */
  export const dismissMobileAppBanner = () => {
    localStorage.setItem('mobileAppBannerDismissed', Date.now().toString());
  };
  
  /**
   * Get viewport dimensions
   * @returns {Object} Width and height of viewport
   */
  export const getViewportDimensions = () => {
    if (typeof window === 'undefined') {
      return { width: 1200, height: 800 }; // Default for SSR
    }
    
    return {
      width: window.innerWidth,
      height: window.innerHeight
    };
  };
  
  /**
   * Check if device supports touch
   * @returns {boolean} True if touch is supported
   */
  export const isTouchDevice = () => {
    if (typeof window === 'undefined') return false;
    
    return 'ontouchstart' in window || 
           navigator.maxTouchPoints > 0 || 
           navigator.msMaxTouchPoints > 0;
  };
  
  /**
   * Get device information for analytics
   * @returns {Object} Device information
   */
  export const getDeviceInfo = () => {
    const viewport = getViewportDimensions();
    
    return {
      isMobile: isMobile(),
      isTablet: isTablet(),
      isTouch: isTouchDevice(),
      platform: detectDevice(),
      viewport,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : '',
      screen: typeof window !== 'undefined' ? {
        width: window.screen.width,
        height: window.screen.height,
        colorDepth: window.screen.colorDepth
      } : null
    };
  };